// src/modules/Admin/pages/Jobs/JobApplications.js
import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import styled from 'styled-components';
import { supabase } from '../../../../config/supabase';
import {
  FaArrowLeft,
  FaEnvelope,
  FaPhone,
  FaLinkedin,
  FaGlobe,
  FaStar,
  FaRegStar,
  FaEllipsisV,
  FaUser,
  FaCheck,
  FaTimes,
  FaComment,
  FaExternalLinkAlt
} from 'react-icons/fa';

// Styled Components
const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const Header = styled.div`
  margin-bottom: 24px;
`;

const BackLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: #666;
  text-decoration: none;
  font-size: 14px;
  margin-bottom: 12px;
  transition: color 200ms;

  &:hover {
    color: #111;
  }
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px;
`;

const Title = styled.h1`
  font-size: 24px;
  font-weight: 700;
  color: #111;
`;

const StatsRow = styled.div`
  display: flex;
  gap: 24px;
  margin-top: 8px;

  span {
    font-size: 14px;
    color: #666;

    strong {
      color: #111;
    }
  }
`;

const FiltersBar = styled.div`
  display: flex;
  gap: 12px;
  margin-bottom: 24px;
  flex-wrap: wrap;
`;

const FilterButton = styled.button`
  padding: 10px 16px;
  background: ${props => props.$active ? '#111' : 'white'};
  color: ${props => props.$active ? 'white' : '#666'};
  border: 1px solid ${props => props.$active ? '#111' : '#e5e5e5'};
  border-radius: 20px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 200ms;

  &:hover {
    background: ${props => props.$active ? '#333' : '#f5f5f5'};
  }
`;

const ApplicationsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const ApplicationCard = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  overflow: hidden;
  border: 2px solid ${props => props.$selected ? '#111' : 'transparent'};
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px;
  border-bottom: 1px solid #f0f0f0;
`;

const ApplicantInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const Avatar = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 600;
  font-size: 18px;
`;

const ApplicantDetails = styled.div`
  .name {
    font-weight: 600;
    color: #111;
    font-size: 15px;
  }

  .date {
    font-size: 12px;
    color: #999;
    margin-top: 2px;
  }
`;

const StatusBadge = styled.span`
  padding: 4px 10px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;

  ${props => {
    switch (props.$status) {
      case 'new':
        return `background: #dbeafe; color: #1e40af;`;
      case 'reviewing':
        return `background: #fef3c7; color: #92400e;`;
      case 'interview':
        return `background: #e0e7ff; color: #4338ca;`;
      case 'offer':
        return `background: #d1fae5; color: #065f46;`;
      case 'hired':
        return `background: #10b981; color: white;`;
      case 'rejected':
        return `background: #fee2e2; color: #991b1b;`;
      default:
        return `background: #f3f4f6; color: #6b7280;`;
    }
  }}
`;

const CardBody = styled.div`
  padding: 20px;
`;

const ContactLinks = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 16px;
`;

const ContactLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: #f5f5f5;
  color: #555;
  text-decoration: none;
  border-radius: 6px;
  font-size: 13px;
  transition: all 200ms;

  &:hover {
    background: #e5e5e5;
    color: #111;
  }

  svg {
    font-size: 12px;
  }
`;

const CoverLetter = styled.div`
  margin-top: 12px;
  padding: 12px;
  background: #fafafa;
  border-radius: 8px;
  font-size: 13px;
  color: #555;
  line-height: 1.6;
  max-height: 100px;
  overflow: hidden;
  position: relative;

  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 40px;
    background: linear-gradient(transparent, #fafafa);
  }
`;

const CardFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  background: #fafafa;
  border-top: 1px solid #f0f0f0;
`;

const Rating = styled.div`
  display: flex;
  gap: 4px;
`;

const StarButton = styled.button`
  border: none;
  background: none;
  cursor: pointer;
  font-size: 16px;
  color: ${props => props.$active ? '#f59e0b' : '#ddd'};
  transition: color 200ms;

  &:hover {
    color: #f59e0b;
  }
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 8px;
`;

const ActionButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  background: ${props => props.$variant === 'success' ? '#d1fae5' : props.$variant === 'danger' ? '#fee2e2' : '#f5f5f5'};
  color: ${props => props.$variant === 'success' ? '#065f46' : props.$variant === 'danger' ? '#991b1b' : '#666'};
  border-radius: 6px;
  cursor: pointer;
  transition: all 200ms;

  &:hover {
    transform: scale(1.1);
  }
`;

const StatusSelect = styled.select`
  padding: 8px 12px;
  border: 1px solid #e5e5e5;
  border-radius: 6px;
  font-size: 13px;
  background: white;
  cursor: pointer;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  background: white;
  border-radius: 12px;
  grid-column: 1 / -1;

  svg {
    font-size: 48px;
    color: #ddd;
    margin-bottom: 16px;
  }

  h3 {
    font-size: 18px;
    font-weight: 600;
    color: #333;
    margin-bottom: 8px;
  }

  p {
    font-size: 14px;
    color: #666;
  }
`;

const Toast = styled.div`
  position: fixed;
  bottom: 24px;
  right: 24px;
  padding: 16px 24px;
  background: ${props => props.$type === 'error' ? '#dc2626' : '#111'};
  color: white;
  border-radius: 8px;
  font-size: 14px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2);
  z-index: 100;
`;

const JobApplications = () => {
  const { id: jobId } = useParams();
  const [job, setJob] = useState(null);
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');
  const [toast, setToast] = useState(null);

  useEffect(() => {
    fetchData();
  }, [jobId]);

  const fetchData = async () => {
    try {
      // Buscar vaga
      const { data: jobData, error: jobError } = await supabase
        .from('jobs')
        .select('*')
        .eq('id', jobId)
        .single();

      if (jobError) throw jobError;
      setJob(jobData);

      // Buscar candidaturas
      const { data: appData, error: appError } = await supabase
        .from('job_applications')
        .select('*')
        .eq('job_id', jobId)
        .order('created_at', { ascending: false });

      if (appError) throw appError;
      setApplications(appData || []);
    } catch (error) {
      console.error('Erro ao buscar dados:', error);
      showToast('Erro ao carregar candidaturas', 'error');
    } finally {
      setLoading(false);
    }
  };

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  const handleStatusChange = async (appId, newStatus) => {
    try {
      const { error } = await supabase
        .from('job_applications')
        .update({ status: newStatus })
        .eq('id', appId);

      if (error) throw error;

      setApplications(apps =>
        apps.map(app => app.id === appId ? { ...app, status: newStatus } : app)
      );

      showToast('Status atualizado');
    } catch (error) {
      console.error('Erro ao atualizar status:', error);
      showToast('Erro ao atualizar status', 'error');
    }
  };

  const handleRating = async (appId, rating) => {
    try {
      const { error } = await supabase
        .from('job_applications')
        .update({ rating })
        .eq('id', appId);

      if (error) throw error;

      setApplications(apps =>
        apps.map(app => app.id === appId ? { ...app, rating } : app)
      );
    } catch (error) {
      console.error('Erro ao salvar avaliação:', error);
    }
  };

  const getInitials = (name) => {
    return name
      .split(' ')
      .map(word => word[0])
      .slice(0, 2)
      .join('')
      .toUpperCase();
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusLabel = (status) => {
    const labels = {
      'new': 'Nova',
      'reviewing': 'Em Análise',
      'interview': 'Entrevista',
      'offer': 'Proposta',
      'hired': 'Contratado',
      'rejected': 'Rejeitado'
    };
    return labels[status] || status;
  };

  const filteredApplications = filterStatus === 'all'
    ? applications
    : applications.filter(app => app.status === filterStatus);

  // Stats
  const stats = {
    total: applications.length,
    new: applications.filter(a => a.status === 'new').length,
    reviewing: applications.filter(a => a.status === 'reviewing').length,
    interview: applications.filter(a => a.status === 'interview').length
  };

  if (loading) {
    return (
      <Container>
        <p>Carregando...</p>
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <BackLink to="/admin/vagas">
          <FaArrowLeft />
          Voltar para vagas
        </BackLink>
        <TitleRow>
          <div>
            <Title>Candidaturas - {job?.title}</Title>
            <StatsRow>
              <span><strong>{stats.total}</strong> total</span>
              <span><strong>{stats.new}</strong> novas</span>
              <span><strong>{stats.reviewing}</strong> em análise</span>
              <span><strong>{stats.interview}</strong> entrevistas</span>
            </StatsRow>
          </div>
        </TitleRow>
      </Header>

      <FiltersBar>
        <FilterButton
          $active={filterStatus === 'all'}
          onClick={() => setFilterStatus('all')}
        >
          Todas ({applications.length})
        </FilterButton>
        <FilterButton
          $active={filterStatus === 'new'}
          onClick={() => setFilterStatus('new')}
        >
          Novas ({stats.new})
        </FilterButton>
        <FilterButton
          $active={filterStatus === 'reviewing'}
          onClick={() => setFilterStatus('reviewing')}
        >
          Em Análise
        </FilterButton>
        <FilterButton
          $active={filterStatus === 'interview'}
          onClick={() => setFilterStatus('interview')}
        >
          Entrevista
        </FilterButton>
        <FilterButton
          $active={filterStatus === 'hired'}
          onClick={() => setFilterStatus('hired')}
        >
          Contratados
        </FilterButton>
        <FilterButton
          $active={filterStatus === 'rejected'}
          onClick={() => setFilterStatus('rejected')}
        >
          Rejeitados
        </FilterButton>
      </FiltersBar>

      {filteredApplications.length === 0 ? (
        <EmptyState>
          <FaUser />
          <h3>Nenhuma candidatura encontrada</h3>
          <p>
            {applications.length === 0
              ? 'Esta vaga ainda não recebeu candidaturas.'
              : 'Nenhuma candidatura corresponde ao filtro selecionado.'
            }
          </p>
        </EmptyState>
      ) : (
        <ApplicationsGrid>
          {filteredApplications.map(app => (
            <ApplicationCard key={app.id}>
              <CardHeader>
                <ApplicantInfo>
                  <Avatar>{getInitials(app.name)}</Avatar>
                  <ApplicantDetails>
                    <div className="name">{app.name}</div>
                    <div className="date">{formatDate(app.created_at)}</div>
                  </ApplicantDetails>
                </ApplicantInfo>
                <StatusBadge $status={app.status}>
                  {getStatusLabel(app.status)}
                </StatusBadge>
              </CardHeader>

              <CardBody>
                <ContactLinks>
                  <ContactLink href={`mailto:${app.email}`}>
                    <FaEnvelope />
                    {app.email}
                  </ContactLink>

                  {app.phone && (
                    <ContactLink href={`tel:${app.phone}`}>
                      <FaPhone />
                      {app.phone}
                    </ContactLink>
                  )}

                  {app.linkedin_url && (
                    <ContactLink href={app.linkedin_url} target="_blank" rel="noopener noreferrer">
                      <FaLinkedin />
                      LinkedIn
                    </ContactLink>
                  )}

                  {app.portfolio_url && (
                    <ContactLink href={app.portfolio_url} target="_blank" rel="noopener noreferrer">
                      <FaGlobe />
                      Portfolio
                    </ContactLink>
                  )}
                </ContactLinks>

                {app.cover_letter && (
                  <CoverLetter>
                    {app.cover_letter}
                  </CoverLetter>
                )}
              </CardBody>

              <CardFooter>
                <Rating>
                  {[1, 2, 3, 4, 5].map(star => (
                    <StarButton
                      key={star}
                      $active={app.rating >= star}
                      onClick={() => handleRating(app.id, star)}
                    >
                      {app.rating >= star ? <FaStar /> : <FaRegStar />}
                    </StarButton>
                  ))}
                </Rating>

                <StatusSelect
                  value={app.status}
                  onChange={(e) => handleStatusChange(app.id, e.target.value)}
                >
                  <option value="new">Nova</option>
                  <option value="reviewing">Em Análise</option>
                  <option value="interview">Entrevista</option>
                  <option value="offer">Proposta</option>
                  <option value="hired">Contratado</option>
                  <option value="rejected">Rejeitado</option>
                </StatusSelect>
              </CardFooter>
            </ApplicationCard>
          ))}
        </ApplicationsGrid>
      )}

      {toast && (
        <Toast $type={toast.type}>
          {toast.message}
        </Toast>
      )}
    </Container>
  );
};

export default JobApplications;
